import { Injectable } from "@angular/core";
import { createStore, select, setProps, withProps } from "@ngneat/elf";
import { localStorageStrategy, persistState } from "@ngneat/elf-persist-state";
import { Permission } from "app/_shared/types/models/role";
import { debounceTime, combineLatest, map } from "rxjs";
import { AppUser } from "app/_shared/types/models/user";
import { Data } from "@angular/router";

interface AppState {
  isLoading: boolean;
  isCollapsed: boolean;
  user: AppUser;
  permissions: Permission[];
  error: boolean;
  status: number;
  routeData: Data;
}

export const store = createStore(
  { name: "app" },
  withProps<AppState>({
    isLoading: true,
    isCollapsed: false,
    user: null,
    permissions: [],
    error: false,
    status: null,
    routeData: null,
  }),
);

export const persist = persistState(store, {
  key: "app",
  storage: localStorageStrategy,
  source: () => store.pipe(debounceTime(1000)),
});

@Injectable({ providedIn: "root" })
export class AppStore {
  user = () => store.getValue().user;
  permissions = () => store.getValue().permissions;
  hasError = () => !!store.getValue().error;

  isLoading$ = store.pipe(select((state) => state.isLoading));
  isCollapsed$ = store.pipe(select((state) => state.isCollapsed));
  error$ = store.pipe(select((state) => ({ hasError: state.error, status: state.status })));
  user$ = store.pipe(select((state) => state.user));
  permissions$ = store.pipe(select((state) => state.permissions));
  hasPermission$ = combineLatest([
    store.pipe(select((state) => state.routeData)),
    store.pipe(select((state) => state.permissions)),
  ]).pipe(map(([data, permissions]) => data["permission"] || permissions.includes(data["permission"])));

  setState(state: Partial<AppState>) {
    store.update(setProps(state));
  }
}
